// /* eslint-disable jsx-a11y/anchor-is-valid */
import { FC, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { Link, useNavigate } from 'react-router-dom'
import { IReportDto } from '../Report.model'
import { PreviewCard } from '../../../common/component/PreviewCard'
import { SettingModalOption, SettingOption } from '../../../common/component/DropdownMenu'
import { CustomInputRenderer } from '../../../common/component/CustomInputRenderer'
import { useReportDowloadAcceptanceByIdMutation, useReportDowloadByIdMutation, useReportShareByIdMutation } from '../Report.api'
import { SubSectionHeader } from '../../../common/component/SubSectionHeader'
import { ShareConfirmationModal } from '../../../common/component/modal/ShareConfirmationModal'
import { EMAIL_REGEX } from '../../../util/Regex'
import { MessageType } from '../../../common/component/MessageToast'
import { getLanguage, getTimeZone, parseLocalDate } from '../../../util/Locale'
import { FREQUENCY } from '../../../util/Constants'
import { ClientEmbedered } from '../../client/components/ClientEmbedered'
import { AssetEmbedered } from '../../asset/components/AssetEmbedered'
import { TicketEmbedered } from '../../helpdesk/ticket/components/TicketEmbedered'

interface Props {
  report?: IReportDto
  loading: boolean
  setRowSelected: (rowId?: string) => void
  setToastEntry: ([MessageType, string]) => void
}

const ReportPreview: FC<Props> = ({report, loading, setRowSelected, setToastEntry}: Props) => {
  const intl = useIntl()
  const navigate = useNavigate()
  const settingOptions: SettingOption[] = [
    {
      label: intl.formatMessage({id: 'UTIL.DETAILS'}),
      icon: 'bi-book',
      action: () => navigate(`/reports/${report?.id}`),
    },
    {
      label: intl.formatMessage({id: 'UTIL.EDIT'}),
      icon: 'bi-pencil',
      action: () => navigate(`/reports/${report?.id}/edit`),
    },
    {
      label: intl.formatMessage({id: 'UTIL.REPORT.ACCEPTANCE.EXPORT'}),
      icon: 'bi-filetype-pdf',
      action: () => downloadAcceptancePdf(report?.id),
    },
    {
      label: intl.formatMessage({id: 'UTIL.REPORT.CONTENT.EXPORT'}),
      icon: 'bi-filetype-pdf',
      action: () => downloadPdf(report?.id),
    },
  ]
  const shareSettingOption: SettingModalOption = {
    label: intl.formatMessage({id: 'UTIL.SHARE'}),
    icon: 'bi-envelope-at',
    targetModal: '#report_share_confirmation_modal',
  }

  const didRequestShare = useRef(false)
  const didFetchDownload = useRef(false)

  const [error, setError] = useState<string | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)
  const [reportDownloadAcceptanceById] = useReportDowloadAcceptanceByIdMutation()
  const [reportDownloadById] = useReportDowloadByIdMutation()
  const [reportShareById] = useReportShareByIdMutation()

  const downloadAcceptancePdf = async (reportId) => {
    try {
      if (reportId) {
        setIsLoading(true)
        if (!didFetchDownload.current) {
          const downloadResponse = await reportDownloadAcceptanceById({
            id: reportId,
            header: true,
            i18n: getLanguage(),
            timeZone: getTimeZone(),
          }).unwrap()
          if (downloadResponse) {
            const url = downloadResponse.url
            const link = document.createElement('a')
            link.target = '_blank'
            link.href = url
            link.download = downloadResponse.file
            link.click()
          }
        }
      }
    } catch (e: any) {
      setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
    } finally {
      setIsLoading(false)
    }
  }

  const downloadPdf = async (reportId) => {
    try {
      if (reportId) {
        setIsLoading(true)
        if (!didFetchDownload.current) {
          const downloadResponse = await reportDownloadById({
            id: reportId,
            header: false,
            i18n: getLanguage(),
            timeZone: getTimeZone()
          }).unwrap()
          if (downloadResponse) {
            const url = downloadResponse.url
            const link = document.createElement('a')
            link.target = '_blank'
            link.href = url
            link.download = downloadResponse.file
            link.click()
          }
        }
      }
    } catch (e: any) {
      setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
    } finally {
      setIsLoading(false)
    }
  }

  const shareSubmitOnClickHandler = async (recipients: string[], comments?: string) => {
    try {
      if (report && !didRequestShare.current && recipients.length > 0) {
        setIsLoading(true)
        const response: any = await reportShareById({
          id: report.id,
          to: recipients,
          i18n: getLanguage(),
          timeZone: getTimeZone(),
          comments,
        }).unwrap()
        if (!response || response.error) {
          setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
        } else {
          setToastEntry([
            MessageType.SUCCESS,
            intl
              .formatMessage({id: 'UTIL.SHARE.SUCCESS.MESSAGE'})
              .replace(/%s/g, intl.formatMessage({id: 'UTIL.REPORT'})),
          ])
        }
      }
    } catch (e: any) {
      setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
    } finally {
      setIsLoading(false)
    }
  }

  const getContactToList = (report: IReportDto): string[] => {
    const toList: string[] = []
    if (report.contacts) {
      report.contacts.map((contact) => {
        if (contact.email) {
          const value = `${contact.email}`.toLowerCase()
          if (!toList.includes(value)) {
            toList.push(value)
          }
        } else if (contact.details) {
          contact.details
            .filter(
              (detail) => detail.type === 'EMAIL' && detail.value && detail.value.match(EMAIL_REGEX)
            )
            .map((detail) => {
              const value = `${detail.value}`.toLowerCase()
              if (!toList.includes(value)) {
                toList.push(value)
              }
            })
        }
      })
    }
    return toList
  }

  return (
    <>
      <PreviewCard
        loading={loading}
        title={
          report
            ? `${parseLocalDate(report.createdAt, intl.formatMessage({id: 'I18N.LOCALE'}))} - ${
                report.client.displayName
              }`
            : ''
        }
        subTitle={report?.sequence}
        pathOnClickHandler={`/reports/${report?.id}`}
        closeOnClickHandler={() => setRowSelected(undefined)}
        settingOptions={settingOptions}
        shareSettingOption={shareSettingOption}
        settingsLoading={isLoading}
        content={
          report && (
            <div>
              <div className='row mt-10'>
                <div className='px-5'>
                  <div className='row mb-5'>
                    <ClientEmbedered client={report.client} />
                    {report.asset && <AssetEmbedered asset={report.asset} />}
                  </div>
                </div>
              </div>

              {report.ticketReferences && report.ticketReferences.length > 0 && (
                <div className='row mb-5'>
                  <SubSectionHeader
                    separator={false}
                    title={intl.formatMessage({id: 'REPORT.WORK_ORDER_REQUEST.COMPLETE.LABEL'})}
                  />
                  <div className='px-5'>
                    <div className='row mb-5'>
                      {report.ticketReferences.map((ticket) => (
                        <TicketEmbedered key={ticket.id} ticketId={ticket.id!} />
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className='row'>
                <SubSectionHeader
                  separator={false}
                  title={intl.formatMessage({id: 'UTIL.GENERAL'})}
                />

                <div className='px-5'>
                  <div className='row mb-5'>
                    <CustomInputRenderer
                      label={intl.formatMessage({id: 'UTIL.UPDATED_DATE'})}
                      value={parseLocalDate(
                        report.updatedAt,
                        intl.formatMessage({id: 'I18N.LOCALE'})
                      )}
                    ></CustomInputRenderer>

                    <CustomInputRenderer
                      label={intl.formatMessage({id: 'UTIL.ID'})}
                      value={`${report.sequence}`}
                    ></CustomInputRenderer>

                    <CustomInputRenderer
                      label={intl.formatMessage({id: 'UTIL.REPORT'})}
                      object={
                        <span className='fs-6 ms-3 text-dark'>
                          <Link className='' to={`/reports/${report.id}`}>
                            {`${parseLocalDate(report.createdAt)} - ${report.client.displayName}`}
                          </Link>
                        </span>
                      }
                    ></CustomInputRenderer>

                    <CustomInputRenderer
                      label={intl.formatMessage({id: 'UTIL.SERVICE_DESK'})}
                      value={`${
                        report.serviceDesk
                          ? intl.formatMessage({id: 'UTIL.YES'})
                          : intl.formatMessage({id: 'UTIL.NO'})
                      }`}
                    ></CustomInputRenderer>

                    <CustomInputRenderer
                      label={intl.formatMessage({id: 'UTIL.STATUS'})}
                      value={
                        report.status
                          ? intl.formatMessage({
                              id: `REPORT.STATUS.${report.status}`,
                            })
                          : ''
                      }
                    ></CustomInputRenderer>

                    <CustomInputRenderer
                      label={intl.formatMessage({id: 'UTIL.DATE_TIME_START'})}
                      value={parseLocalDate(report.startedAt, '', '', {
                        hours: true,
                        minutes: true,
                        seconds: false,
                        millis: false,
                      })}
                    ></CustomInputRenderer>

                    <CustomInputRenderer
                      label={intl.formatMessage({id: 'UTIL.DATE_TIME_END'})}
                      value={parseLocalDate(report.endedAt, '', '', {
                        hours: true,
                        minutes: true,
                        seconds: false,
                        millis: false,
                      })}
                    ></CustomInputRenderer>

                    <CustomInputRenderer
                      label={intl.formatMessage({id: 'UTIL.SHARED_VIA_EMAIL'})}
                      value={`${
                        report.shared
                          ? intl.formatMessage({id: 'UTIL.YES'})
                          : intl.formatMessage({id: 'UTIL.NO'})
                      }`}
                    ></CustomInputRenderer>

                    <CustomInputRenderer
                      label={intl.formatMessage({id: 'UTIL.VISIT_TYPE'})}
                      value={report.visitType}
                    ></CustomInputRenderer>

                    <CustomInputRenderer
                      label={intl.formatMessage({id: 'UTIL.CATEGORY'})}
                      value={report.category}
                    ></CustomInputRenderer>

                    <CustomInputRenderer
                      label={intl.formatMessage({id: 'UTIL.SERVICE_FREQUENCY'})}
                      value={`${
                        report.frequencyCode === FREQUENCY.OTHER
                          ? report.frequency
                          : intl.formatMessage({
                              id: `DROPDOWN.FREQUENCY.${report.frequencyCode}`,
                            })
                      }`}
                    ></CustomInputRenderer>
                  </div>
                </div>

                <SubSectionHeader
                  title={intl.formatMessage({id: 'UTIL.LOCATION'})}
                  separator={false}
                />

                <div className='px-5'>
                  <div className='row mb-5'>
                    <CustomInputRenderer
                      label={intl.formatMessage({id: 'UTIL.COUNTRY'})}
                      value={
                        report.countryCode
                          ? intl.formatMessage({
                              id: `DROPDOWN.COUNTRY.${report.countryCode}`,
                            })
                          : ''
                      }
                    ></CustomInputRenderer>

                    <CustomInputRenderer
                      label={intl.formatMessage({id: 'UTIL.ADDRESS'})}
                      value={report.address}
                    ></CustomInputRenderer>
                  </div>
                </div>

                <SubSectionHeader
                  separator={false}
                  title={intl.formatMessage({id: 'UTIL.METRICS'})}
                />

                <div className='px-5'>
                  <div className='row mb-5'>
                    <CustomInputRenderer
                      label={intl.formatMessage({id: 'UTIL.ATTACHMENTS'})}
                      value={`${report.totalAttachment || 0}`}
                    ></CustomInputRenderer>
                  </div>
                </div>
              </div>
            </div>
          )
        }
      ></PreviewCard>

      {report && (
        <ShareConfirmationModal
          id='report_share_confirmation_modal'
          title={intl
            .formatMessage({id: 'UTIL.SHARE.CONFIRMATION.TITLE'})
            .replace(
              /%s/g,
              `${new Date(Date.parse(report.createdAt)).toLocaleString()} - ${
                report.client.displayName
              } - #${report?.sequence}`
            )}
          description={`${new Date(Date.parse(report.createdAt)).toLocaleString()} - ${
            report.client.displayName
          } - #${report?.sequence}`}
          toListContacts={getContactToList(report)}
          submitOnClickHandler={shareSubmitOnClickHandler}
        ></ShareConfirmationModal>
      )}
    </>
  )
}

export { ReportPreview }
